import React__default, { useState, useRef, useEffect, useCallback } from 'react';
import { useConciergeSettings } from './settings/useBrandclubSettings.js';
import { FloatingButtonWidget } from '@brandclub/bc-widgets';
import { SuggestedMessagePills } from './SuggestedMessagePills.js';
import '../node_modules/tslib/tslib.es6.js';
import { track } from '../analytics/AnalyticsService.js';
import 'branch-sdk';
import '../analytics/consent.js';
import { AnalyticsTrackingEvent } from '../analytics/events.js';
import '../analytics/externalTracking.js';
import '../analytics/fingerprint.js';
import '../analytics/types.js';
import '../analytics/metaPixel.js';
import { useBrandAppConfig } from '../api/cache/appConfigQueryOptions.js';
import { useWidgetVisibilityTracking } from './hooks/useWidgetVisibilityTracking.js';

// Helper to convert RGBA-style shadow to CSS box-shadow string
const getShadowStyle = (state) => {
    const { shadowColor, shadowOpacity, shadowBlur, shadowSpread, shadowOffsetY } = state;
    // Parse the shadow color (hex) and apply opacity
    const r = parseInt(shadowColor.slice(1, 3), 16);
    const g = parseInt(shadowColor.slice(3, 5), 16);
    const b = parseInt(shadowColor.slice(5, 7), 16);
    return `0 ${shadowOffsetY}px ${shadowBlur}px ${shadowSpread}px rgba(${r}, ${g}, ${b}, ${shadowOpacity})`;
};
// Helper to get position styles from widget state (theme editor controlled)
const getPositionStyles = (state) => {
    const styles = {};
    const { position, horizontalOffset, verticalOffset } = state;
    // Vertical position based on theme editor "Position" setting
    if (position.startsWith('top')) {
        styles.top = verticalOffset;
    }
    else {
        styles.bottom = verticalOffset;
    }
    // Horizontal position based on theme editor "Position" setting
    if (position.endsWith('left')) {
        styles.left = horizontalOffset;
    }
    else {
        styles.right = horizontalOffset;
    }
    return styles;
};
const ConciergeFloatingButton = ({ widgetState, isConciergeEnabled = true, hideOnConciergePage = false, isMaximized = false, onSuggestedPillClick, }) => {
    const { toggle, isOpen, close, open } = widgetState;
    const [isHovered, setIsHovered] = useState(false);
    const [shouldAnimate, setShouldAnimate] = useState(false);
    const wasOpenRef = useRef(isOpen);
    // Get settings for z_index, hide_default_launcher, and custom_launcher_icon
    // (positioning is now controlled via theme editor through FloatingButtonWidget state)
    const settings = useConciergeSettings();
    // Get brand config for appName (for tracking)
    const { data: brandConfig } = useBrandAppConfig();
    // Widget visibility tracking - track when button becomes visible
    const { trackImmediate } = useWidgetVisibilityTracking({
        appName: brandConfig === null || brandConfig === void 0 ? void 0 : brandConfig.appName,
        widgetType: 'floating_button',
        enabled: isConciergeEnabled && !settings.hide_default_launcher,
    });
    // Track widget impression and session start when button is rendered and visible
    // The floating button is always visible when rendered, so track immediately
    useEffect(() => {
        // Only track once we have the appName loaded (to ensure accurate tracking)
        if ((brandConfig === null || brandConfig === void 0 ? void 0 : brandConfig.appName) && isConciergeEnabled && !settings.hide_default_launcher) {
            trackImmediate();
        }
    }, [brandConfig === null || brandConfig === void 0 ? void 0 : brandConfig.appName, isConciergeEnabled, settings.hide_default_launcher, trackImmediate]);
    // Handle pill click - track and notify parent, then open widget
    const handlePillClick = useCallback((message, index) => {
        // Track the click event
        track(AnalyticsTrackingEvent.CONCIERGE_SUGGESTED_PILL_CLICKED, {
            message,
            messageIndex: index,
            source: 'minimized_floating_button',
        });
        // Notify parent to set the initial query (uses props-based approach for reliability)
        onSuggestedPillClick === null || onSuggestedPillClick === void 0 ? void 0 : onSuggestedPillClick(message);
        // Open the widget
        open();
    }, [onSuggestedPillClick, open]);
    // Track when widget closes to trigger entrance animation
    useEffect(() => {
        if (wasOpenRef.current && !isOpen) {
            // Widget just closed, animate button in
            setShouldAnimate(true);
            const timer = setTimeout(() => {
                setShouldAnimate(false);
            }, 350); // Match animation duration
            return () => clearTimeout(timer);
        }
        wasOpenRef.current = isOpen;
    }, [isOpen]);
    // Listen for escape key to close the widget
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                close();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, close]);
    // Hide button when maximized, not enabled, on concierge page, or if hide_default_launcher is set
    // When widget is open (but not maximized), show button with chevron icon
    if (isMaximized || hideOnConciergePage || !isConciergeEnabled || settings.hide_default_launcher) {
        return null;
    }
    return (React__default.createElement(FloatingButtonWidget, { widgetId: "floating-button", displayName: "Floating Button" }, (state) => (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("style", null, `
            @keyframes button-pop-in {
              0% {
                opacity: 0;
                transform: scale(0.5);
              }
              70% {
                opacity: 1;
                transform: scale(1.1);
              }
              100% {
                opacity: 1;
                transform: scale(1);
              }
            }
            .concierge-button-animate-in {
              animation: button-pop-in 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
            }
          `),
        !isOpen && (React__default.createElement(SuggestedMessagePills, { onPillClick: handlePillClick, position: state.position, buttonSize: state.size, horizontalOffset: state.horizontalOffset, verticalOffset: state.verticalOffset })),
        React__default.createElement("button", { onClick: toggle, className: shouldAnimate ? 'concierge-button-animate-in' : '', style: Object.assign(Object.assign({ position: 'fixed' }, getPositionStyles(state)), { width: state.size, height: state.size, backgroundColor: isHovered ? state.backgroundColorHover : state.backgroundColor, borderRadius: `${state.borderRadius}%`, boxShadow: getShadowStyle(state), cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: settings.z_index, transition: shouldAnimate ? 'none' : `transform ${state.animationDuration}ms ease, background-color ${state.animationDuration}ms ease`, border: 'none', outline: 'none', transform: !shouldAnimate && isHovered ? `scale(${state.hoverScale})` : !shouldAnimate ? 'scale(1)' : undefined }), onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), "aria-label": isOpen ? 'Close Concierge' : 'Open Concierge' }, isOpen ? (React__default.createElement("svg", { width: "19", height: "11", viewBox: "0 0 19 11", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
            React__default.createElement("path", { d: "M1.25 1.25L9.1543 9.1543L17.0586 1.25", stroke: state.iconColor, strokeWidth: "2.5", strokeLinecap: "round", strokeLinejoin: "round" }))) : settings.custom_launcher_icon ? (React__default.createElement("img", { src: settings.custom_launcher_icon, alt: "", style: { width: state.iconSize, height: state.iconSize } })) : (React__default.createElement("svg", { width: state.iconSize, height: state.iconSize, viewBox: "0 0 22 21", fill: "none", xmlns: "http://www.w3.org/2000/svg", style: { color: state.iconColor } },
            React__default.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M15.4136 0.86523C15.6886 0.33825 16.2385 0 16.8436 0C17.5138 0 18.1155 0.41595 18.3528 1.04398L19.0641 2.92734C19.0641 2.92938 19.0641 2.93146 19.0678 2.93317C19.0678 2.93451 19.0711 2.93589 19.0711 2.93659L20.9545 3.6468C21.5836 3.88362 22 4.48481 22 5.15614C22 5.72264 21.7041 6.23893 21.2332 6.5287C21.728 7.70122 22 8.97786 22 10.3123C22 15.9902 17.0844 20.6245 11 20.6245C9.15053 20.6245 7.40432 20.1948 5.87502 19.4382C4.58938 20.0418 3.17629 20.3976 1.99379 20.6C1.34462 20.7111 0.687877 20.4375 0.309707 19.8975C-0.0684628 19.3574 -0.102773 18.6476 0.220347 18.078C0.739387 17.1527 1.07631 16.1081 1.29603 15.1692C0.467657 13.7204 0.00037716 12.067 0.00037716 10.3123C0.00037716 4.63434 4.91591 0 11 0C12.5709 0 14.0627 0.30902 15.4136 0.86523H15.4136ZM11 8.93732C11.7599 8.93732 12.375 9.55329 12.375 10.3123C12.375 11.0712 11.7599 11.6872 11 11.6872C10.2405 11.6872 9.62526 11.0712 9.62526 10.3123C9.62526 9.55329 10.2405 8.93732 11 8.93732ZM5.84414 8.93732C6.60342 8.93732 7.2189 9.55329 7.2189 10.3123C7.2189 11.0712 6.60342 11.6872 5.84414 11.6872C5.08425 11.6872 4.4689 11.0712 4.4689 10.3123C4.4689 9.55329 5.08426 8.93732 5.84414 8.93732ZM16.6201 1.52896C16.6547 1.43619 16.7439 1.375 16.8436 1.375C16.9434 1.375 17.0327 1.43619 17.0673 1.52896L17.7752 3.41269C17.8473 3.59763 17.9539 3.76535 18.0949 3.90524C18.236 4.04516 18.4009 4.15341 18.5864 4.22323L20.4702 4.93337C20.563 4.96843 20.6248 5.05712 20.6248 5.15612C20.6248 5.25512 20.563 5.3438 20.4702 5.37884L18.5864 6.08905C18.4009 6.15881 18.236 6.26712 18.0949 6.40704C17.9539 6.5469 17.8473 6.71466 17.7752 6.89956L17.0673 8.78329C17.0327 8.87609 16.9434 8.93731 16.8436 8.93731C16.7439 8.93731 16.6547 8.87609 16.6201 8.78329L15.912 6.89956C15.8401 6.71465 15.7332 6.5469 15.5925 6.40704C15.4514 6.26712 15.2865 6.15881 15.101 6.08905L13.2172 5.37884C13.1242 5.34381 13.0626 5.25512 13.0626 5.15612C13.0626 5.05712 13.1242 4.96844 13.2172 4.93337L15.101 4.22323C15.2865 4.15341 15.4514 4.04516 15.5925 3.90524C15.7332 3.76535 15.8399 3.59762 15.912 3.41269L16.6201 1.52896Z", fill: "currentColor" }))))))));
};

export { ConciergeFloatingButton };
//# sourceMappingURL=ConciergeFloatingButton.js.map
